import React, { useEffect, useId, useRef, useState } from "react";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaUniversity,
} from "react-icons/fa";
import { DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS } from "./commissionBillToUtils";

function CheckboxRow({ id, label, checked, onChange, disabled }) {
  return (
    <label className="cms-commission-billto-popover__check" htmlFor={id}>
      <span>{label}</span>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}

function CommissionBillToContactPopover({
  open,
  anchorRef,
  onClose,
  providerId,
  profile = {},
  printOptions = DEFAULT_COMMISSION_BILL_TO_PRINT_OPTIONS,
  onPrintOptionsChange,
  onBillingAddressSaved,
  savingAddress = false,
  readOnly = false,
  printOptionsReadOnly,
  addressReadOnly,
  savingPrintOptions = false,
}) {
  const panelId = useId();
  const panelRef = useRef(null);
  const [billingDraft, setBillingDraft] = useState("");
  const checksReadOnly =
    (printOptionsReadOnly ?? readOnly) || savingPrintOptions;
  const billingReadOnly = addressReadOnly ?? readOnly;
  const canEditAddress = Boolean(!billingReadOnly && providerId);

  useEffect(() => {
    if (open) {
      setBillingDraft(String(profile?.address || "").trim());
    }
  }, [open, profile?.address]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      const anchor = anchorRef?.current;
      const panel = panelRef.current;
      if (!panel) return;
      if (panel.contains(event.target)) return;
      if (anchor && anchor.contains(event.target)) return;
      onClose?.();
    };

    const handleKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  const name = String(profile?.fullName || "").trim() || "—";
  const email = String(profile?.email || "").trim() || "—";
  const phone = String(profile?.phone || "").trim() || "—";
  const country = String(profile?.country || "").trim() || "—";
  const campus = String(profile?.campus || profile?.city || "").trim() || "—";

  const handleSaveAddress = async () => {
    if (!canEditAddress) return;
    await onBillingAddressSaved?.(billingDraft);
  };

  const addressDirty =
    billingDraft.trim() !== String(profile?.address || "").trim();

  return (
    <div
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-label="Provider billing contact"
      className="cms-commission-billto-popover"
    >
      <div className="cms-commission-billto-popover__header">
        <div className="cms-commission-billto-popover__title-row">
          <span className="cms-commission-billto-popover__avatar" aria-hidden>
            {name.slice(0, 1).toUpperCase()}
          </span>
          <div className="cms-commission-billto-popover__title">{name}</div>
        </div>
      </div>

      <div className="cms-commission-billto-popover__body">
        <div className="cms-commission-billto-popover__main">
          <div className="cms-commission-billto-popover__meta-grid">
            <div className="cms-commission-billto-popover__row">
              <FaEnvelope
                className="cms-commission-billto-popover__icon"
                aria-hidden
              />
              <div>
                <div className="cms-commission-billto-popover__label">Email</div>
                <div className="cms-commission-billto-popover__value">{email}</div>
              </div>
            </div>
            <div className="cms-commission-billto-popover__row">
              <FaPhone
                className="cms-commission-billto-popover__icon"
                aria-hidden
              />
              <div>
                <div className="cms-commission-billto-popover__label">Phone</div>
                <div className="cms-commission-billto-popover__value">{phone}</div>
              </div>
            </div>
            <div className="cms-commission-billto-popover__row">
              <FaUniversity
                className="cms-commission-billto-popover__icon"
                aria-hidden
              />
              <div>
                <div className="cms-commission-billto-popover__label">Country</div>
                <div className="cms-commission-billto-popover__value">
                  {country}
                </div>
              </div>
            </div>
            <div className="cms-commission-billto-popover__row">
              <FaMapMarkerAlt
                className="cms-commission-billto-popover__icon"
                aria-hidden
              />
              <div>
                <div className="cms-commission-billto-popover__label">Campus</div>
                <div className="cms-commission-billto-popover__value">{campus}</div>
              </div>
            </div>
          </div>

          <div className="cms-commission-billto-popover__address-block">
            <div className="cms-commission-billto-popover__section-title">
              Billing and delivery address
            </div>
            {canEditAddress ? (
              <>
                <textarea
                  className="cms-commission-billto-popover__textarea"
                  rows={3}
                  value={billingDraft}
                  onChange={(e) => setBillingDraft(e.target.value)}
                  placeholder="Enter complete billing address"
                  aria-label="Billing and delivery address"
                />
                <button
                  type="button"
                  className="cms-commission-billto-popover__save-btn"
                  disabled={savingAddress || !addressDirty}
                  onClick={handleSaveAddress}
                >
                  {savingAddress ? "Saving…" : "Save address to provider"}
                </button>
              </>
            ) : (
              <div className="cms-commission-billto-popover__address">
                {String(profile?.address || "").trim() || "—"}
              </div>
            )}
          </div>
        </div>

        <div className="cms-commission-billto-popover__aside cms-commission-billto-popover__section--checks">
          <div className="cms-commission-billto-popover__section-title">
            Show on printed invoice
          </div>
          <CheckboxRow
            id={`${panelId}-email`}
            label="Email"
            checked={printOptions.showEmail}
            disabled={checksReadOnly}
            onChange={(checked) =>
              onPrintOptionsChange?.({ ...printOptions, showEmail: checked })
            }
          />
          <CheckboxRow
            id={`${panelId}-phone`}
            label="Phone"
            checked={printOptions.showPhone}
            disabled={checksReadOnly}
            onChange={(checked) =>
              onPrintOptionsChange?.({ ...printOptions, showPhone: checked })
            }
          />
          <CheckboxRow
            id={`${panelId}-country`}
            label="Country"
            checked={printOptions.showCountry}
            disabled={checksReadOnly}
            onChange={(checked) =>
              onPrintOptionsChange?.({ ...printOptions, showCountry: checked })
            }
          />
          <CheckboxRow
            id={`${panelId}-campus`}
            label="Campus"
            checked={printOptions.showCampus}
            disabled={checksReadOnly}
            onChange={(checked) =>
              onPrintOptionsChange?.({ ...printOptions, showCampus: checked })
            }
          />
          <CheckboxRow
            id={`${panelId}-address`}
            label="Billing address"
            checked={printOptions.showBillingAddress}
            disabled={checksReadOnly}
            onChange={(checked) =>
              onPrintOptionsChange?.({
                ...printOptions,
                showBillingAddress: checked,
              })
            }
          />
        </div>
      </div>
    </div>
  );
}

export default CommissionBillToContactPopover;
